import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_NUMBER = "491798067007";

// WhatsApp SVG Logo
const WhatsAppLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const ContactSection = () => {
  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 md:py-32 bg-tiktok-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-tiktok-cyan/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-tiktok-pink/5 rounded-full blur-[150px]" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-sm font-semibold uppercase tracking-[0.25em] text-tiktok-cyan mb-6 px-4 py-2 bg-tiktok-cyan/10 rounded-full border border-tiktok-cyan/20">
              Jetzt Kontakt aufnehmen
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6">
              Autoankauf{' '}
              <span className="bg-gradient-to-r from-tiktok-cyan to-tiktok-pink bg-clip-text text-transparent">
                deutschlandweit & europaweit
              </span>
            </h2>
          </motion.div>

          {/* SEO Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-tiktok-surface/50 rounded-3xl border border-white/10 p-8 md:p-12 mb-10"
          >
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                <strong className="text-white">PKW Ankauf 24/7</strong> ist Ihr zuverlässiger Partner für den 
                professionellen Autoankauf in ganz Deutschland und Europa. Wir kaufen Fahrzeuge aller Art – ob Gebrauchtwagen, 
                Unfallfahrzeuge, Autos mit Motorschaden oder Fahrzeuge ohne TÜV.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Unser Service zeichnet sich durch <strong className="text-tiktok-cyan">schnelle Bewertung</strong>, 
                {' '}<strong className="text-tiktok-cyan">faire Preise</strong> und eine 
                {' '}<strong className="text-tiktok-cyan">unkomplizierte Abwicklung</strong> aus. 
                Sie erhalten innerhalb kürzester Zeit ein verbindliches Angebot für Ihr Fahrzeug. 
                Ob kurze oder lange Strecke – wir holen Ihr Auto kostenlos ab, egal wo Sie sich befinden.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Kontaktieren Sie uns jetzt für eine kostenlose und unverbindliche Fahrzeugbewertung. 
                Wir sind <strong className="text-white">24 Stunden am Tag, 7 Tage die Woche</strong> für 
                Sie erreichbar – persönlich, schnell und zuverlässig.
              </p>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="w-full sm:w-auto"
            >
              <Button
                className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold text-lg px-10 py-7 rounded-2xl transition-all hover:scale-105 active:scale-95"
              >
                <WhatsAppLogo className="w-6 h-6 mr-3" />
                WhatsApp schreiben
              </Button>
            </a>

            <a
              href={`tel:+${WHATSAPP_NUMBER}`}
              data-testid="contact-call-btn"
              className="w-full sm:w-auto"
            >
              <Button
                className="w-full sm:w-auto bg-tiktok-surface hover:bg-tiktok-elevated border border-white/10 text-white font-bold text-lg px-10 py-7 rounded-2xl transition-all hover:scale-105 active:scale-95"
              >
                <Phone className="w-6 h-6 mr-3" />
                Jetzt anrufen
              </Button>
            </a>
          </motion.div>

          {/* Trust Note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center text-gray-500 text-sm mt-8"
          >
            Kostenlose Beratung • Unverbindliches Angebot • Schnelle Rückmeldung
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
